import axios from 'axios'
import { authUrls } from '@/configs/endpoints'
import { SUCCESS_CODES } from '@/configs/constants'
import { getAccessToken, setAccessToken, clearSession } from '@/plugins/tokenStorage'

// In dev an absolute VUE_APP_API would send every request straight to the API host, making
// responses cross-site — the backend's `SameSite=Lax` refresh_token cookie is then dropped. Fall
// back to a relative base so requests go through the dev-server proxy (see vue.config.js) and stay
// same-origin. Production is served from the API origin, so the absolute base is used as-is.
const usesDevProxy = process.env.NODE_ENV === 'development' && /^https?:\/\//i.test(process.env.VUE_APP_API || '')
const MAIN_URL = usesDevProxy ? '/' : process.env.VUE_APP_API || '/'
const LOGIN_PATH = '/login/enter-phone'
const PUBLIC_PAGES = ['/login', '/404']

const instance = axios.create({
  baseURL: MAIN_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  },
  validateStatus: () => true // never throw; callers branch on the returned `code`
})

// Absolute URLs target external services (e.g. Directus CMS), not our API.
const isExternalUrl = url => /^https?:\/\//i.test(url || '')

// Attach our Bearer token + refresh cookie only to our own API. External requests are left
// untouched — sending our token or credentials cross-origin breaks CORS and leaks the token.
instance.interceptors.request.use(config => {
  if (isExternalUrl(config.url)) return config
  config.withCredentials = true // send the httpOnly refresh cookie (matters for cross-origin API envs)
  const token = getAccessToken()
  if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`
  if (process.env.VUE_APP_TZ_HEADER === 'true') {
    config.headers['X-TzName'] = Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  return config
})

// Single in-flight refresh shared across concurrent 401s (e.g. App.vue loadData Promise.all).
let refreshPromise = null

// Refresh a little early so a request never goes out on a token about to expire mid-flight.
const TOKEN_EXPIRY_SKEW_MS = 5000

// Set once a refresh returns a token that *still* reads as expired to us: that means the client
// clock disagrees with the server, so we stop trusting our own expiry check (it would otherwise
// refresh on every request) and lean on the reactive 401 path instead.
let clockExpiryUnreliable = false

// Memoize the parsed expiry per token string so a burst of requests doesn't re-decode the same JWT.
let memoToken = null
let memoExpiryMs = NaN

// Decode a JWT's `exp` (ms since epoch); NaN for opaque/malformed tokens. JWT segments are
// base64url-encoded, so map `-`/`_` back to `+`/`/` before atob, which only accepts standard base64.
function tokenExpiryMs(token) {
  if (token === memoToken) return memoExpiryMs
  memoToken = token
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    memoExpiryMs = typeof exp === 'number' ? exp * 1000 : NaN
  } catch (error) {
    memoExpiryMs = NaN
  }
  return memoExpiryMs
}

// Whether the token has passed its expiry (minus the skew). Unparseable tokens report not-expired
// and fall through to the reactive 401 path below.
function isTokenExpired(token) {
  const expiryMs = tokenExpiryMs(token)
  return !Number.isNaN(expiryMs) && Date.now() >= expiryMs - TOKEN_EXPIRY_SKEW_MS
}

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await instance.post(authUrls.refresh)
        if (SUCCESS_CODES.includes(res.status) && res.data?.access_token) {
          setAccessToken(res.data.access_token)
          // A freshly issued token that already reads as expired means our clock is off — stop the
          // proactive expiry check from firing a refresh on every subsequent request.
          clockExpiryUnreliable = isTokenExpired(res.data.access_token)
          return true
        }
        return false
      } catch (error) {
        return false
      } finally {
        refreshPromise = null
      }
    })()
  }
  return refreshPromise
}

function redirectToLogin() {
  clearSession()
  const onPublicPage = PUBLIC_PAGES.some(page => location.pathname.startsWith(page))
  if (!onPublicPage) {
    window.location = `${LOGIN_PATH}?path=${encodeURIComponent(location.pathname)}`
  }
}

// Build an axios config from the superapi-style client options ({ body, responseType, headers, ... }).
function buildConfig(method, url, clientOptions = {}) {
  const { body, ...rest } = clientOptions
  const config = { method, url, ...rest }
  if (body !== undefined) config.data = body
  return config
}

async function request(method, url, clientOptions) {
  const config = buildConfig(method, url, clientOptions)
  // Authenticated calls to our own API are the only ones eligible for a token refresh.
  const isOwnApiCall = !isExternalUrl(url) && url !== authUrls.refresh
  const token = getAccessToken()

  // Proactive refresh: if a shared refresh is already running, or our token is already expired,
  // settle a single refresh before sending. This serializes startup — App.loadData and the pages'
  // own mounted() requests can't coordinate, so instead of each firing on the stale token and
  // provoking its own 401, they all block on the same refresh. We send regardless of the outcome:
  // the reactive branch below is the single place that decides retry-vs-redirect, so a valid-token
  // request that merely joined an in-flight refresh is never force-logged-out here.
  let didRefresh = false
  if (isOwnApiCall && token && (refreshPromise || (!clockExpiryUnreliable && isTokenExpired(token)))) {
    await refreshAccessToken()
    didRefresh = true
  }

  let res = await instance.request(config)

  // Reactive fallback: the server rejected our token. Refresh once and retry — but not if we
  // already refreshed for this request (a second refresh won't help), in which case the session is
  // unrecoverable and we redirect to login.
  const canRefresh = res.status === 401 && isOwnApiCall && getAccessToken() && !config._retried
  if (canRefresh) {
    const refreshed = didRefresh ? false : await refreshAccessToken()
    if (refreshed) {
      // Drop the stale Authorization header so the interceptor re-attaches the fresh token.
      const retryConfig = { ...config, _retried: true, headers: { ...config.headers } }
      delete retryConfig.headers.Authorization
      res = await instance.request(retryConfig)
    } else {
      redirectToLogin()
    }
  }

  return { code: res.status, data: res.data, headers: res.headers }
}

const api = {
  get: (url, clientOptions) => request('get', url, clientOptions),
  post: (url, clientOptions) => request('post', url, clientOptions),
  put: (url, clientOptions) => request('put', url, clientOptions),
  delete: (url, clientOptions) => request('delete', url, clientOptions),
  patch: (url, clientOptions) => request('patch', url, clientOptions),
  head: (url, clientOptions) => request('head', url, clientOptions)
}

export default api
